/**
 * Thin fetch wrapper: prepends the control-plane base URL, attaches the
 * access token when we have one, and throws a descriptive Error on non-2xx
 * responses.
 *
 * Access tokens live for 15 minutes; on a 401 we transparently POST the
 * stored refresh token to `/v1/auth/refresh`, update the session, and retry
 * the original request ONCE. Concurrent 401s share a single in-flight
 * refresh promise so we never consume two refresh tokens for one expired
 * access token. If the refresh itself 401s (or the retried call 401s again),
 * we clear the session and let the caller propagate.
 */

import { authSession, clearAuthSession, setAuthSession } from './session';

/**
 * Typed error thrown by [`api`] and the other request helpers when the CP
 * returns a non-2xx response. `.message` is set to the plain `detail`
 * string from the CP's problem+json body when parseable — so route code
 * that just does `setErr((e as Error).message)` shows readable text
 * ("wrong_code", "email_not_verified", "trial_period_days exceeds 30")
 * instead of the raw JSON blob.
 *
 * Callers that need to branch on status or the untranslated detail can
 * still do `e instanceof ApiError` and read the fields.
 */
export class ApiError extends Error {
  status: number;
  detail: string;
  title: string;
  type: string;
  rawBody: string;
  constructor(status: number, rawBody: string) {
    let detail = rawBody;
    let title = '';
    let type = '';
    try {
      const parsed = JSON.parse(rawBody) as {
        detail?: string;
        title?: string;
        type?: string;
      };
      if (typeof parsed.detail === 'string' && parsed.detail.length > 0) {
        detail = parsed.detail;
      }
      if (typeof parsed.title === 'string') title = parsed.title;
      if (typeof parsed.type === 'string') type = parsed.type;
    } catch {
      // Non-JSON body — leave detail as the raw body.
    }
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.title = title;
    this.type = type;
    this.rawBody = rawBody;
  }
}

const BASE: string =
  (import.meta.env['VITE_CONTROL_PLANE'] as string | undefined) ??
  'https://api.backstop.family';

/**
 * Shared in-flight refresh promise. When null, no refresh is in progress and
 * the next 401 kicks one off; when non-null, later 401s await the same
 * promise instead of racing to spend a second refresh token.
 */
let inflight: Promise<boolean> | null = null;

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * POST the current refresh token to `/v1/auth/refresh`. On success, rotates
 * the tokens in `authSession` via `setAuthSession` and resolves `true`.
 * Resolves `false` on any failure (missing session, network error, non-2xx).
 * Callers are responsible for calling `clearAuthSession()` when this returns
 * `false`; the helper deliberately leaves session state alone so it can be
 * unit-tested independently of the "abandon the session" decision.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (inflight !== null) return inflight;
  const s = authSession.value;
  if (s === null) return false;

  inflight = (async () => {
    try {
      const resp = await fetch(`${BASE}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: s.refreshToken }),
      });
      if (!resp.ok) return false;
      const text = await resp.text();
      if (text.length === 0) return false;
      const body = JSON.parse(text) as RefreshResponse;
      setAuthSession({
        ...s,
        accessToken: body.access_token,
        refreshToken: body.refresh_token,
      });
      return true;
    } catch {
      return false;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Build the headers Object for an authenticated request, reading the
 * *current* `authSession.value` each time so a mid-flight token rotation is
 * picked up on retry.
 */
function buildHeaders(init: RequestInit): Headers {
  const headers = new Headers(init.headers);
  const s = authSession.value;
  if (s !== null) {
    headers.set('authorization', `Bearer ${s.accessToken}`);
  }
  if (
    !headers.has('content-type') &&
    init.body !== undefined &&
    init.body !== null
  ) {
    headers.set('content-type', 'application/json');
  }
  return headers;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers: buildHeaders(init),
  });

  // On 401, try to refresh once and retry the original request. If either
  // the refresh or the retried call fails, clear the session and throw so
  // callers see the auth failure and can redirect to login.
  if (resp.status === 401 && authSession.value !== null) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      clearAuthSession();
      const text = await resp.text();
      throw new ApiError(resp.status, text);
    }
    resp = await fetch(`${BASE}${path}`, {
      ...init,
      headers: buildHeaders(init),
    });
    if (resp.status === 401) {
      clearAuthSession();
    }
  }

  if (!resp.ok) {
    const text = await resp.text();
    throw new ApiError(resp.status, text);
  }

  // 204 No Content (and any 2xx with an empty body) has no JSON to parse.
  // Callers of empty-response endpoints should pass `T = null`.
  if (resp.status === 204) return null as T;
  const text = await resp.text();
  if (text.length === 0) return null as T;
  return JSON.parse(text) as T;
}

/**
 * PATCH `path` with a JSON body. Used for small updates like renaming an
 * endpoint. Throws on non-2xx. Returns the parsed JSON body, or `null` on
 * 204/empty responses.
 */
export async function apiPatchJson<T>(
  path: string,
  body: unknown,
): Promise<T | null> {
  const headers = new Headers();
  const s = authSession.value;
  if (s !== null) {
    headers.set('authorization', `Bearer ${s.accessToken}`);
  }
  headers.set('content-type', 'application/json');

  const resp = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new ApiError(resp.status, text);
  }
  if (resp.status === 204) return null;
  const text = await resp.text();
  if (text.length === 0) return null;
  return JSON.parse(text) as T;
}

/**
 * DELETE `path`. Throws on non-2xx.
 */
export async function apiDelete(path: string): Promise<void> {
  const headers = new Headers();
  const s = authSession.value;
  if (s !== null) {
    headers.set('authorization', `Bearer ${s.accessToken}`);
  }
  const resp = await fetch(`${BASE}${path}`, { method: 'DELETE', headers });
  if (!resp.ok) {
    const text = await resp.text();
    throw new ApiError(resp.status, text);
  }
}

/**
 * POST raw bytes to `path`. Used for family-key wraps and other
 * already-encrypted blobs where JSON wrapping would just cost bytes.
 * Throws on non-2xx. Returns nothing (server responds 204).
 */
export async function apiPostBytes(
  path: string,
  body: Uint8Array,
): Promise<void> {
  await sendBytes('POST', path, body);
}

/** PUT raw bytes to `path`; upserts an already-encrypted blob. */
export async function apiPutBytes(
  path: string,
  body: Uint8Array,
): Promise<void> {
  await sendBytes('PUT', path, body);
}

async function sendBytes(
  method: 'POST' | 'PUT',
  path: string,
  body: Uint8Array,
): Promise<void> {
  const headers = new Headers();
  const s = authSession.value;
  if (s !== null) {
    headers.set('authorization', `Bearer ${s.accessToken}`);
  }
  headers.set('content-type', 'application/octet-stream');

  const resp = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body as BodyInit,
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new ApiError(resp.status, text);
  }
}
